import { useState } from 'react';

import { Paper } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { ConfirmationDialog } from '@/features/Dashboard/pages/Edit/components';
import { useDeletePastExport } from '@/features/DataHub/api';
import { usePastExportDetails } from '@/features/DataHub/hooks';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import { useExportRequest } from '@/features/DataHub/modules/Export/context/exportContexts';
import { useExportApiKey } from '@/features/DataHub/modules/Export/hooks/useExportApiKey';
import {
  CLIENT_SIDE_DOWNLOAD_TYPES,
  performClientSideDownload
} from '@/features/DataHub/modules/Export/services/ClientSideDownloadService';
import {
  ANALYTICS_EVENTS,
  buildPastExportEventData
} from '@/features/DataHub/modules/Export/services/ExportAnalyticsService';
import { ExportItemActions } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemActions';
import { ExportItemDetails } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemDetails';
import { ExportItemHeader } from '@/features/DataHub/pages/PastExports/components/ExportItem/ExportItemHeader';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import useUserDetails from '@/hooks/useUserDetails';

export const ExportItem = ({ item }: { item: ExportItemType }) => {
  const { exportDataWithParams, setExportStatus } = useExportRequest();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const posthog = usePostHog();

  const { data, isLoading } = usePastExportDetails(item.id);
  const { mutate, isPending: isDeleting } = useDeletePastExport();

  const { isUserPremium, isUserExpired } = useUserDetails();
  const apiKey = useExportApiKey();

  const handleDelete = () => {
    setConfirmOpen(false);
    const { eventName, data: eventData } = buildPastExportEventData(
      ANALYTICS_EVENTS.PAST_EXPORT_DELETED,
      item
    );
    mutate(item.id, {
      onSuccess: () => posthog.capture(eventName, eventData)
    });
  };

  const handleRegenerate = async () => {
    if (!data) return;
    setExportStatus('loading');

    // For third-party exports, the concrete type is in exportSubType
    const effectiveType = data.type === 'third-party' ? data.exportSubType : data.type;
    const isClientSideDownload = effectiveType
      ? CLIENT_SIDE_DOWNLOAD_TYPES.has(effectiveType)
      : false;

    try {
      await exportDataWithParams({
        id: item.id,
        name: data.name,
        type: data.type,
        exportSubType: data.exportSubType,
        from: data.from,
        to: data.to,
        includeMetadata: data.metadata === 'true',
        timezone: data.timezone,
        dateFormat: data.dateFormat,
        symbols: data.symbols
      });

      // Server only returns downloadUrl for Excel; other types need client-side download
      if (isClientSideDownload && effectiveType) {
        await performClientSideDownload(effectiveType, data, apiKey);
        setExportStatus('idle');
      }

      const { eventName, data: eventData } = buildPastExportEventData(
        ANALYTICS_EVENTS.PAST_EXPORT_REGENERATED,
        item
      );
      posthog.capture(eventName, eventData);
    } catch {
      setExportStatus('idle');
    }
  };

  const effectiveType = data?.type === 'third-party' ? data.exportSubType : data?.type;
  const option = effectiveType ? (optionById.get(effectiveType) ?? null) : null;
  const isOngoing = item.exportStatus === 'ongoing';

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <ExportItemHeader
        name={item.name}
        status={item.exportStatus}
      />
      <ExportItemDetails
        data={data}
        isLoading={isLoading}
        optionLabel={option?.label}
      />
      <ExportItemActions
        canDelete={!isOngoing || data === undefined}
        canRegenerate={
          (item.exportStatus === 'finished' || item.exportStatus === 'failed') &&
          Boolean(data) &&
          isUserPremium &&
          !isUserExpired
        }
        isDeleting={isDeleting}
        onDelete={() => {
          setConfirmOpen(true);
        }}
        onRegenerate={handleRegenerate}
      />
      <ConfirmationDialog
        cancelText='Cancel'
        confirmText='Delete'
        open={confirmOpen}
        text='This export will be permanently deleted.'
        title='Delete past export?'
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={handleDelete}
      />
    </Paper>
  );
};
